// HR-004 follow-up — Worker Role Master.
// Admin-editable list of worker roles. Each role has a code, name,
// color, type (MACHINE_BOUND / FLOATING / BOTH), and display order.
// The "Add floating role" dropdown + side allocation drawer read from
// this master, merged with the hardcoded defaults for back-compat.

import { useState } from 'react'
import {
  App, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select,
  Space, Table, Tag, Typography,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  workerRolesApi,
  type WorkerRole, type WorkerRoleCreate, type WorkerRolePatch,
} from '@/features/hr/manpowerApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text } = Typography

// Same antd Tag colors the backend validates against
const TAG_COLORS = [
  'default', 'blue', 'cyan', 'geekblue', 'purple', 'magenta', 'pink',
  'red', 'volcano', 'orange', 'gold', 'lime', 'green',
]

const ROLE_TYPE_LABEL: Record<string, string> = {
  MACHINE_BOUND: 'Machine-bound',
  FLOATING: 'Floating / Roving',
  BOTH: 'Both',
}
const ROLE_TYPE_COLOR: Record<string, string> = {
  MACHINE_BOUND: 'blue',
  FLOATING: 'purple',
  BOTH: 'gold',
}


export default function WorkerRoleMasterPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<WorkerRole | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)

  const { data: roles, isLoading } = useQuery({
    queryKey: ['worker-roles', includeInactive],
    queryFn: () => workerRolesApi.list({
      is_active: includeInactive ? undefined : true,
    }),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['worker-roles'] })

  const createMut = useMutation({
    mutationFn: (body: WorkerRoleCreate) => workerRolesApi.create(body),
    onSuccess: () => { message.success('Role added'); invalidate(); setCreateOpen(false) },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkerRolePatch }) =>
      workerRolesApi.patch(id, body),
    onSuccess: () => { message.success('Updated'); invalidate(); setEditing(null) },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Update failed')
    },
  })

  const removeMut = useMutation({
    mutationFn: (id: string) => workerRolesApi.remove(id),
    onSuccess: () => { message.success('Deactivated'); invalidate() },
  })

  const columns = [
    {
      title: 'Code', dataIndex: 'role_code', key: 'code',
      render: (s: string, r: WorkerRole) => (
        <Space size={4}>
          <Tag color={r.color_tag} style={{ fontWeight: 700, fontSize: 11 }}>
            {s}
          </Tag>
          {r.system_seeded && (
            <Tag style={{ fontSize: 9, opacity: 0.7 }}>SYSTEM</Tag>
          )}
        </Space>
      ),
    },
    { title: 'Name', dataIndex: 'role_name', key: 'name' },
    {
      title: 'Type', dataIndex: 'role_type', key: 'type',
      render: (t: string) => (
        <Tag color={ROLE_TYPE_COLOR[t]}>{ROLE_TYPE_LABEL[t] || t}</Tag>
      ),
    },
    {
      title: 'Order', dataIndex: 'display_order', key: 'order',
      render: (n: number) => <Text type="secondary">{n}</Text>,
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'st',
      render: (b: boolean) => (
        <Tag color={b ? 'success' : 'default'}>
          {b ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    ...(canEdit ? [{
      title: '', key: 'actions',
      render: (_: unknown, r: WorkerRole) => (
        <Space size={2}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => setEditing(r)} />
          {!r.system_seeded && (
            <Popconfirm
              title="Deactivate this role?"
              description="Existing allocations stay intact. New allocations won't be able to use this role."
              onConfirm={() => removeMut.mutate(r.id)}
              okText="Deactivate" okButtonProps={{ danger: true }}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ]

  return (
    <div>
      <PageHeader
        title="Worker Role Master"
        subtitle="Add, rename, or deactivate worker roles. Drives the picker dropdowns in Worker Allocation."
        extra={canEdit && (
          <Space>
            <Tag.CheckableTag
              checked={includeInactive}
              onChange={setIncludeInactive}
              style={{ padding: '2px 10px', lineHeight: '20px' }}
            >
              {includeInactive ? '✓ ' : ''}Include inactive
            </Tag.CheckableTag>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}>
              New Role
            </Button>
          </Space>
        )}
      />

      <Card style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          System-seeded roles (marked SYSTEM) can be renamed or
          deactivated but not deleted — they're referenced in historical
          allocations. Custom roles can be fully removed.
        </Text>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns as never}
          dataSource={roles ?? []}
          pagination={false}
          size="small"
        />
      </Card>

      <CreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate(body)}
        submitting={createMut.isPending}
      />
      <EditModal
        role={editing}
        onClose={() => setEditing(null)}
        onSubmit={(id, body) => patchMut.mutate({ id, body })}
        submitting={patchMut.isPending}
      />
    </div>
  )
}


function CreateModal({ open, onClose, onSubmit, submitting }: {
  open: boolean
  onClose: () => void
  onSubmit: (body: WorkerRoleCreate) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={open} onCancel={onClose}
      title="New Worker Role" width={520}
      okText="Create" confirmLoading={submitting}
      onOk={() => form.validateFields().then((v) => {
        onSubmit({
          role_code: v.role_code,
          role_name: v.role_name,
          role_type: v.role_type ?? 'FLOATING',
          color_tag: v.color_tag ?? 'default',
          description: v.description ?? null,
          display_order: v.display_order ?? 200,
        })
      })}
    >
      <Form form={form} layout="vertical" initialValues={{
        role_type: 'FLOATING', color_tag: 'default', display_order: 200,
      }}>
        <Form.Item name="role_code" label="Role Code"
          tooltip="Short identifier in CAPS_UNDERSCORE. Used in worker_allocations.required_role."
          rules={[{ required: true }, {
            pattern: /^[A-Z][A-Z0-9_]*$/,
            message: 'CAPS, digits, underscores only. Start with a letter.',
          }]}>
          <Input placeholder="e.g. SLUBBER_TENTER" />
        </Form.Item>
        <Form.Item name="role_name" label="Role Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Slubber Tenter" />
        </Form.Item>
        <Form.Item name="role_type" label="Role Type" rules={[{ required: true }]}>
          <Select options={[
            { label: 'Machine-bound (pinned to a specific machine/side)', value: 'MACHINE_BOUND' },
            { label: 'Floating / Roving (covers the whole department)', value: 'FLOATING' },
            { label: 'Both (can be allocated either way)', value: 'BOTH' },
          ]} />
        </Form.Item>
        <Form.Item name="color_tag" label="Color (for visual distinction)">
          <Select options={TAG_COLORS.map((c) => ({
            label: (
              <Space size={6}>
                <Tag color={c} style={{ margin: 0 }}>SAMPLE</Tag>
                <Text type="secondary" style={{ fontSize: 11 }}>{c}</Text>
              </Space>
            ),
            value: c,
          }))} />
        </Form.Item>
        <Form.Item name="display_order" label="Display Order"
          tooltip="Lower number = appears first in dropdowns. Existing system roles use 10-190.">
          <InputNumber min={1} max={9999} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="description" label="Description (optional)">
          <Input.TextArea rows={2} placeholder="What does this role do?" />
        </Form.Item>
      </Form>
    </Modal>
  )
}


function EditModal({ role, onClose, onSubmit, submitting }: {
  role: WorkerRole | null
  onClose: () => void
  onSubmit: (id: string, body: WorkerRolePatch) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={!!role} onCancel={onClose}
      title={role ? `Edit ${role.role_code}` : 'Edit'}
      okText="Save" confirmLoading={submitting} width={520}
      onOk={() => {
        if (!role) return
        form.validateFields().then((v) => {
          onSubmit(role.id, {
            role_name: v.role_name,
            role_type: v.role_type,
            color_tag: v.color_tag,
            display_order: v.display_order,
            description: v.description ?? null,
            is_active: v.is_active,
          })
        })
      }}
      destroyOnClose
    >
      {role && (
        <Form form={form} layout="vertical" initialValues={{
          role_name: role.role_name,
          role_type: role.role_type,
          color_tag: role.color_tag,
          display_order: role.display_order,
          description: role.description ?? '',
          is_active: role.is_active,
        }}>
          <Form.Item label="Role Code (immutable)">
            <Input value={role.role_code} disabled />
          </Form.Item>
          <Form.Item name="role_name" label="Role Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role_type" label="Role Type">
            <Select options={[
              { label: 'Machine-bound', value: 'MACHINE_BOUND' },
              { label: 'Floating / Roving', value: 'FLOATING' },
              { label: 'Both', value: 'BOTH' },
            ]} />
          </Form.Item>
          <Form.Item name="color_tag" label="Color">
            <Select options={TAG_COLORS.map((c) => ({
              label: <Space size={6}><Tag color={c}>SAMPLE</Tag>{c}</Space>,
              value: c,
            }))} />
          </Form.Item>
          <Form.Item name="display_order" label="Display Order">
            <InputNumber min={1} max={9999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="Status" valuePropName="checked">
            <Select options={[
              { label: 'ACTIVE', value: true },
              { label: 'INACTIVE', value: false },
            ]} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
